import React from "react";
import CouponInput from "./CouponInput";
import type { AppliedCoupon } from "../types/billing.types";

interface CouponFieldProps {
  /** What the last quote came back carrying, which is the only coupon there
   *  is — see `useQuote`. `null` for an order with none on it. */
  applied: AppliedCoupon | null;
  /** Sends the typed code up with a fresh quote. */
  onApply: (code: string) => Promise<void>;
  /** Prices the same order again with no code on it. */
  onRemove: () => Promise<void>;
  /** Opens the list of coupons on offer. */
  onBrowse: () => void;
  /** A quote is with the server — the whole row stands down. */
  busy?: boolean;
  /** The checkout is paying; nothing about the order may change now. */
  locked?: boolean;
  /** What the row is called. "Offers & coupons" on a paid order; the trial
   *  step calls it what it is there, which is a code to apply and nothing to
   *  browse through. */
  label?: string;
  /**
   * Drops the caption, leaving "View coupons" alone over the box.
   *
   * For the phone's checkout, where this row is the first thing on the page
   * and the panel it changes is directly beneath it: a word over a code field
   * there is a caption on a screen that has not said anything yet. The
   * heading still names the section to a screen reader.
   */
  hideLabel?: boolean;
}

/**
 * The coupon on the order, above the summary it changes.
 *
 * A heading, a way to the coupons this workspace has been given, and the field
 * itself — which is `CouponInput`, shared with the dialog behind "View
 * coupons" so a code typed in either place behaves identically. Everything
 * about what a code is worth belongs to the server; see `CouponInput`.
 */
const CouponField: React.FC<CouponFieldProps> = ({
  applied,
  onApply,
  onRemove,
  onBrowse,
  busy = false,
  locked = false,
  label = "Offers & coupons",
  hideLabel = false,
}) => (
  <section aria-labelledby="coupon-heading">
    {/* `justify-end` with the caption gone, so "View coupons" keeps the right
        edge it sits on rather than sliding across to the left one. */}
    <div
      className={`mb-1.5 flex items-center gap-3 ${
        hideLabel ? "justify-end" : "justify-between"
      }`}
    >
      <h2
        id="coupon-heading"
        className={
          hideLabel
            ? "sr-only"
            : "text-[13px] 3xl:text-[14px] text-ink-muted"
        }
      >
        {label}
      </h2>
      <button
        type="button"
        onClick={onBrowse}
        disabled={locked}
        className="text-[13px] 3xl:text-[14px] text-ink-muted underline-offset-2 transition-colors hover:text-ink hover:underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        View coupons
      </button>
    </div>

    <CouponInput
      applied={applied}
      onApply={onApply}
      onRemove={onRemove}
      busy={busy}
      locked={locked}
    />
  </section>
);

export default CouponField;
